/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx'
import {useState, useEffect} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG, toAbsoluteUrl} from '../../../helpers'
import {IHeader, useLayout} from '../../core'
import {useThemeMode} from '../../../partials/layout/theme-mode/ThemeModeProvider'
import {DefaultTitle} from './page-title/DefaultTitle'
import {Topbar} from './Topbar'
import {MODULE_PRESENTATION, useModule} from '../../../../app/platform/modules'
import {
  DESKTOP_ASIDE_HIDDEN_STORAGE_KEY,
  getStoredDesktopAsideHidden,
  persistDesktopAsideHidden,
} from './asidePreference'

const calculateStickyOffset = (header: IHeader): string => {
  if (header.fixed.desktop && header.fixed.tabletAndMobile) {
    return '{default: "200px", lg: "300px"}'
  }

  if (header.fixed.desktop && !header.fixed.tabletAndMobile) {
    return '{lg: "300px"}'
  }

  if (header.fixed.tabletAndMobile && !header.fixed.desktop) {
    return '{default: "200px", lg: false}'
  }

  return ''
}

const calculateShowSticky = (header: IHeader): boolean => {
  return (
    (header.fixed.desktop && header.fixed.tabletAndMobile) ||
    (header.fixed.desktop && !header.fixed.tabletAndMobile) ||
    (header.fixed.tabletAndMobile && !header.fixed.desktop)
  )
}

const getIsDesktopViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(min-width: 992px)').matches

export function HeaderWrapper() {
  const {config, classes, attributes} = useLayout()
  const {header} = config
  const {layoutWidth} = useThemeMode()
  const {defaultRoute, isModuleSession, module, moduleCode} = useModule()
  const modulePresentation = moduleCode ? MODULE_PRESENTATION[moduleCode] : undefined
  const appTitle = process.env.REACT_APP_TITLE?.toLowerCase()
  const isMenulessPortal =
    !isModuleSession && (appTitle === 'dsos' || appTitle === 'student-clearance')
  const shouldShowAsideToggle = !isMenulessPortal && config.aside?.display !== false
  const [stickyOffset, setStickyOffset] = useState<string>(calculateStickyOffset(header))
  const [showSticky, setShowSticky] = useState<boolean>(calculateShowSticky(header))
  const [isDesktopAsideHidden, setIsDesktopAsideHidden] = useState<boolean>(
    getStoredDesktopAsideHidden
  )
  const [isDesktopViewport, setIsDesktopViewport] = useState<boolean>(getIsDesktopViewport)
  const isDesktopAsideCollapsed =
    isDesktopViewport && layoutWidth === 'full' && isDesktopAsideHidden

  useEffect(() => {
    setStickyOffset(calculateStickyOffset(header))
    setShowSticky(calculateShowSticky(header))
  }, [header])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const {body} = document

    if (shouldShowAsideToggle && isDesktopAsideCollapsed) {
      body.classList.add('aside-hidden')
    } else {
      body.classList.remove('aside-hidden')
    }

    return () => {
      body.classList.remove('aside-hidden')
    }
  }, [isDesktopAsideCollapsed, shouldShowAsideToggle])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }

    const mediaQuery = window.matchMedia('(min-width: 992px)')
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsDesktopViewport(event.matches)
    }

    setIsDesktopViewport(mediaQuery.matches)
    mediaQuery.addEventListener?.('change', handleViewportChange)
    return () => {
      mediaQuery.removeEventListener?.('change', handleViewportChange)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleStoredPreferenceChange = (event: StorageEvent) => {
      if (event.key === DESKTOP_ASIDE_HIDDEN_STORAGE_KEY) {
        setIsDesktopAsideHidden(event.newValue === 'true')
      }
    }

    window.addEventListener('storage', handleStoredPreferenceChange)
    return () => {
      window.removeEventListener('storage', handleStoredPreferenceChange)
    }
  }, [])

  const handleAsideToggle = () => {
    if (isDesktopViewport && layoutWidth === 'full') {
      setIsDesktopAsideHidden((previousValue) => {
        const nextValue = !previousValue
        persistDesktopAsideHidden(nextValue)
        return nextValue
      })
    }
  }

  return (
    <div
      id='kt_header'
      className={clsx('header', classes.header.join(' '))}
      {...attributes.headerMenu}
      data-kt-sticky={showSticky ? 'true' : 'false'}
      data-kt-sticky-name='header'
      data-kt-sticky-offset={stickyOffset}
    >
      <div
        className={clsx(
          classes.headerContainer.join(' '),
          'd-flex flex-stack gap-2 nosi-header-container'
        )}
        id='kt_header_container'
      >
        <DefaultTitle />

        {/* begin::Wrapper */}
        <div className='d-flex align-items-center ms-n2 me-2 nosi-header-primary'>
          {/* begin::Aside mobile toggle */}
          {shouldShowAsideToggle && (
            <button
              type='button'
              className={clsx('btn btn-active-icon-primary nosi-aside-toggle', {
                active: isDesktopAsideCollapsed,
              })}
              id='kt_aside_toggle'
              aria-label='Menüyü aç veya kapat'
              aria-pressed={
                isDesktopViewport && layoutWidth === 'full' ? isDesktopAsideHidden : undefined
              }
              title='Menüyü aç veya kapat; tercihiniz bu cihazda hatırlanır'
              onClick={handleAsideToggle}
            >
              <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-1' />
              <span className='nosi-aside-toggle__label d-none d-xl-inline'>
                {isDesktopAsideHidden ? 'Menüyü göster' : 'Menüyü gizle'}
              </span>
            </button>
          )}
          {/* end::Aside mobile toggle */}

          {/* begin::Logo */}

          {isModuleSession && module && modulePresentation ? (
            <Link to={defaultRoute} className='nosi-header-module-brand d-flex d-lg-none'>
              <span className='nosi-header-module-brand__icon'>
                <KTSVG path={modulePresentation.icon} className='svg-icon-2' />
              </span>
              <span>
                <small>Aktif modül</small>
                <strong>{module.displayName}</strong>
              </span>
            </Link>
          ) : (
            <Link to={defaultRoute} className='d-flex align-items-center d-lg-none'>
              <img
                alt='Gradeurexam'
                src={toAbsoluteUrl('/media/logos/gradeurexam2.png')}
                className='h-50px mt-2 mt-md-0'
              />
            </Link>
          )}
          {/* end::Logo */}
        </div>
        {/* end::Wrapper */}
        <Topbar />
      </div>
    </div>
  )
}
